import React, { useState, useEffect } from 'react';
import {
  Webhook, Plus, Search, Play, Pause, Trash2, CheckCircle, XCircle,
  Clock, Send, MoreVertical, AlertCircle, RefreshCw, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { FieldHelp, InlineHelpBanner } from '@/components/help';
import { toast } from 'sonner';
import webhooksApi from '@/api/webhooks';

const statusColors = {
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  error: 'bg-red-500',
};

const deliveryStatusColors = {
  pending: 'bg-amber-500',
  delivered: 'bg-emerald-500',
  failed: 'bg-red-500',
};

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEventsDialog, setShowEventsDialog] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [],
    secret: '',
  });

  useEffect(() => {
    loadWebhooks();
    loadEventTypes();
  }, []);

  const loadEventTypes = async () => {
    try {
      const result = await webhooksApi.getEventTypeOptions();
      if (result.success) {
        setEventTypeOptions(result.data);
      }
    } catch (error) {
      console.error('Failed to load event types:', error);
    }
  };

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      const result = await webhooksApi.list();
      if (result.success) {
        setWebhooks(result.data);
      }
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (webhookId) => {
    try {
      const result = await webhooksApi.getEvents(webhookId);
      if (result.success) {
        setEvents(result.data);
      }
    } catch (error) {
      toast.error('Failed to load events');
    }
  };

  const handleCreate = async () => {
    if (!newWebhook.name.trim() || !newWebhook.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }

    if (newWebhook.events.length === 0) {
      toast.error('Select at least one event type');
      return;
    }

    try {
      const result = await webhooksApi.create(newWebhook);
      if (result.success) {
        toast.success('Webhook created!');
        setShowCreateDialog(false);
        setNewWebhook({ name: '', url: '', events: [], secret: '' });
        loadWebhooks();
      } else {
        toast.error(result.detail || 'Failed to create webhook');
      }
    } catch (error) {
      toast.error('Failed to create webhook');
    }
  };

  const handleActivate = async (id) => {
    try {
      const result = await webhooksApi.activate(id);
      if (result.success) {
        toast.success('Webhook activated');
        loadWebhooks();
      }
    } catch (error) {
      toast.error('Failed to activate webhook');
    }
  };

  const handlePause = async (id) => {
    try {
      const result = await webhooksApi.pause(id);
      if (result.success) {
        toast.success('Webhook paused');
        loadWebhooks();
      }
    } catch (error) {
      toast.error('Failed to pause webhook');
    }
  };

  const handleTest = async (id) => {
    try {
      const result = await webhooksApi.test(id);
      if (result.success) {
        toast.success('Test payload sent!');
      } else {
        toast.error(result.detail || 'Test failed');
      }
    } catch (error) {
      toast.error('Failed to send test');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    try {
      const result = await webhooksApi.delete(id);
      if (result.success) {
        toast.success('Webhook deleted');
        loadWebhooks();
      }
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleRetryEvent = async (eventId) => {
    try {
      const result = await webhooksApi.retryEvent(eventId);
      if (result.success) {
        toast.success('Event retry triggered');
        if (showEventsDialog) {
          loadEvents(showEventsDialog.id);
        }
      }
    } catch (error) {
      toast.error('Failed to retry event');
    }
  };

  const toggleEventType = (eventType) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventType)
        ? prev.events.filter(e => e !== eventType)
        : [...prev.events, eventType]
    }));
  };

  const filteredWebhooks = webhooks.filter(w =>
    w.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.url?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Webhooks</h1>
          <p className="text-muted-foreground mt-1">Connect to external services and tools</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-webhook-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Webhook
        </Button>
      </div>

      <InlineHelpBanner id="webhooks-intro" title="What is a webhook?">
        A webhook is a URL that LinkHub calls when something happens in your
        account — a QR scan, a new lead, a short-URL click. You give us the
        URL of a service that can receive the event (Zapier, Make, your own
        endpoint), pick which events you care about, and we POST a JSON
        payload each time one fires. Use them to push activity into Slack,
        a CRM, or a Google Sheet without writing integration code.
      </InlineHelpBanner>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Webhooks</p>
                <p className="text-2xl font-bold">{webhooks.length}</p>
              </div>
              <Webhook className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {webhooks.filter(w => w.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paused</p>
                <p className="text-2xl font-bold text-amber-600">
                  {webhooks.filter(w => w.status === 'paused').length}
                </p>
              </div>
              <Pause className="w-8 h-8 text-amber-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Error</p>
                <p className="text-2xl font-bold text-red-600">
                  {webhooks.filter(w => w.status === 'error').length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search webhooks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="search-webhooks"
        />
      </div>

      {/* Webhooks List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-6 bg-muted rounded w-1/3 mb-2" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredWebhooks.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Webhook className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No webhooks yet</h3>
            <p className="text-muted-foreground mb-4">Create your first webhook to connect with external services</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="webhooks-list">
          {filteredWebhooks.map(webhook => (
            <Card key={webhook.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${statusColors[webhook.status] || 'bg-gray-500'} flex items-center justify-center text-white`}>
                      <Webhook className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium">{webhook.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs bg-muted px-2 py-0.5 rounded">{webhook.url}</code>
                        <Badge className={`${statusColors[webhook.status]} text-white text-xs`}>
                          {webhook.status}
                        </Badge>
                      </div>
                      {webhook.events && webhook.events.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {webhook.events.slice(0, 3).map(event => (
                            <Badge key={event} variant="outline" className="text-xs">
                              {event}
                            </Badge>
                          ))}
                          {webhook.events.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{webhook.events.length - 3}</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowEventsDialog(webhook);
                        loadEvents(webhook.id);
                      }}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Events
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleTest(webhook.id)}>
                      <Send className="w-4 h-4 mr-1" />
                      Test
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {webhook.status === 'active' ? (
                          <DropdownMenuItem onClick={() => handlePause(webhook.id)}>
                            <Pause className="w-4 h-4 mr-2" /> Pause
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleActivate(webhook.id)}>
                            <Play className="w-4 h-4 mr-2" /> Activate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDelete(webhook.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Webhook</DialogTitle>
            <DialogDescription>Send event notifications to your URL</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Webhook Name *</Label>
              <Input
                id="name"
                placeholder="Zapier Integration"
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                data-testid="webhook-name-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="url">Endpoint URL *</Label>
                <FieldHelp text="The HTTPS URL we POST event payloads to. Zapier and Make give you one when you create a new 'Webhook' trigger; for your own backend, use a publicly-reachable URL that accepts JSON." />
              </div>
              <Input
                id="url"
                placeholder="https://hooks.zapier.com/..."
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                data-testid="webhook-url-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Event Types *</Label>
                <FieldHelp text="Tick the events that should be sent to this endpoint. Most integrations want one or two events — picking 'everything' makes the receiving service noisy and harder to debug." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {eventTypeOptions.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 p-2 border rounded hover:bg-muted cursor-pointer">
                    <Checkbox
                      checked={newWebhook.events.includes(opt.value)}
                      onCheckedChange={() => toggleEventType(opt.value)}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="secret">Secret (optional)</Label>
                <FieldHelp text="When set, every webhook delivery includes an X-LinkHub-Signature header computed as HMAC-SHA256(secret, body). Your receiver can verify the request really came from LinkHub by recomputing the signature. Skip if you trust the URL to be hard to guess." />
              </div>
              <Input
                id="secret"
                type="password"
                placeholder="Signing secret for verification"
                value={newWebhook.secret}
                onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Used to sign payloads for verification</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-webhook-submit">
              Create Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Events Dialog */}
      {showEventsDialog && (
        <Dialog open={!!showEventsDialog} onOpenChange={() => setShowEventsDialog(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Webhook Events - {showEventsDialog.name}</DialogTitle>
              <DialogDescription>Recent deliveries and their status</DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-auto">
              {events.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No events yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {events.map(event => (
                    <div key={event.id} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-3">
                        <Badge className={`${deliveryStatusColors[event.status]} text-white text-xs`}>
                          {event.status}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{event.event_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {event.created_at ? new Date(event.created_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                      {event.status === 'failed' && (
                        <Button variant="outline" size="sm" onClick={() => handleRetryEvent(event.id)}>
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Retry
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
